import {useMutation} from '@tanstack/react-query';
import {useRef} from 'react';
import {v4 as uuid} from 'uuid';
import {avatarFetchers} from '@contentlab/api/avatars';
import type {
  AiAvatarGenerateScriptRequest,
  AiAvatarGenerateScriptResponse,
  ScriptConfiguration,
} from '@contentlab/api/avatars/types';
import type {
  ProcessEndedEventParams,
  ProcessStartedEventParams,
} from '@contentlab/hooks/analytics/use-process-event/useProcessEvent';

const ENDPOINT = '/api/v1/ai_avatars/generate_scripts';
const PROCESS_NAME = 'script_generation';

interface UseGenerateAvatarScriptsOptions {
  onSuccess?: (
    data: AiAvatarGenerateScriptResponse,
    requestParams: AiAvatarGenerateScriptRequest
  ) => Promise<void> | void;
  onError?: (error: Error) => void;
  processEventHandlers: {
    started: (params: ProcessStartedEventParams) => void;
    ended: (params: ProcessEndedEventParams) => void;
  };
}

export interface GenerateScriptsParams {
  assetId: string;
  scriptConfigurations: ScriptConfiguration[];
  source?: string; // Optional source override for analytics
}

function useGenerateAvatarScripts(options: UseGenerateAvatarScriptsOptions) {
  const {onSuccess, onError, processEventHandlers} = options;
  const timingRef = useRef<{
    processId: string;
    interactionId: string;
    params: GenerateScriptsParams;
  } | null>(null);

  return useMutation<
    AiAvatarGenerateScriptResponse,
    Error,
    GenerateScriptsParams
  >({
    mutationFn: (params: GenerateScriptsParams) => {
      const processId = uuid();
      const interactionId = uuid();

      // Store process info for onSuccess/onError
      timingRef.current = {
        processId,
        interactionId,
        params,
      };

      // Track process started
      processEventHandlers.started({
        endpoint: ENDPOINT,
        process_id: processId,
        process_name: PROCESS_NAME,
        asset_id: params.assetId,
        original_id: params.assetId,
        interaction_id: interactionId,
        process_details: JSON.stringify({
          scriptConfigurations: params.scriptConfigurations,
        }),
        source: params.source,
      });

      // Map frontend configs to backend format
      const backendParams: AiAvatarGenerateScriptRequest = {
        assetId: params.assetId,
        scriptConfigurations: params.scriptConfigurations,
      };
      return avatarFetchers.generateScripts(backendParams);
    },
    onSuccess: (data, variables) => {
      // Track success
      if (timingRef.current) {
        const {processId, params} = timingRef.current;
        processEventHandlers.ended({
          endpoint: ENDPOINT,
          process_id: processId,
          process_name: PROCESS_NAME,
          original_id: params.assetId,
          result: 'success',
          process_details: JSON.stringify({
            source: params.source,
            scriptConfigurations: params.scriptConfigurations,
          }),
        });
        timingRef.current = null;
      }

      // Call user's onSuccess callback
      if (onSuccess) {
        const backendParams: AiAvatarGenerateScriptRequest = {
          assetId: variables.assetId,
          scriptConfigurations: variables.scriptConfigurations,
        };
        onSuccess(data, backendParams);
      }
    },
    onError: (error) => {
      // Track failure with error
      if (timingRef.current) {
        const {processId, params} = timingRef.current;
        const errorMessage =
          error instanceof Error ? error.message : 'Script generation failed';
        processEventHandlers.ended({
          endpoint: ENDPOINT,
          process_id: processId,
          process_name: PROCESS_NAME,
          original_id: params.assetId,
          result: 'failure',
          error: errorMessage,
          process_details: JSON.stringify({
            source: params.source,
            scriptConfigurations: params.scriptConfigurations,
          }),
        });
        timingRef.current = null;
      }

      // Call user's onError callback
      if (onError) {
        onError(error);
      }
    },
  });
}

export default useGenerateAvatarScripts;
